import { type ComponentType } from 'react';

import { Text } from '@atlaskit/primitives/compiled';

import { FormattedMessage } from 'react-intl';

import { type JSX } from 'react/jsx-runtime';

import { messages } from './messages';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

interface StepComponentProps {
	dismiss: () => void;
	next: () => void;
	done: () => void;
	stepIndex: number;
	totalSteps: number;
}

export const tour: { [selector: string]: ComponentType<StepComponentProps> } = {
	// Step 1: Explore risk by category
	'#risk-assessment-categories-spotlight-target': ({
		dismiss,
		next,
		stepIndex,
		totalSteps,
	}: StepComponentProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={dismiss} placement="bottom-end" isVisible={true}>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage {...messages.exploreCategoriesHeading} />
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl autoFocus={false} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Text>
								<FormattedMessage {...messages.exploreCategoriesDescription} />
							</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightStepCount>
								<FormattedMessage
									{...messages.progressIndicator}
									values={{
										currentStep: stepIndex + 1,
										totalSteps,
									}}
								/>
							</SpotlightStepCount>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={next}>
									<FormattedMessage {...messages.nextButton} />
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},

	// Step 2: Mitigate risk
	'#risk-assessment-mitigation-steps-spotlight-target': ({
		dismiss,
		done,
		stepIndex,
		totalSteps,
	}: StepComponentProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={dismiss} placement="bottom-end" isVisible={true}>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage {...messages.mitigateRiskHeading} />
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl autoFocus={false} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Text>
								<FormattedMessage {...messages.mitigateRiskDescription} />
							</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightStepCount>
								<FormattedMessage
									{...messages.progressIndicator}
									values={{
										currentStep: stepIndex + 1,
										totalSteps,
									}}
								/>
							</SpotlightStepCount>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={done}>
									<FormattedMessage {...messages.doneButton} />
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
};

export interface RiskAssessmentSpotlightTourProps {
	dismiss: () => void;
	next: () => void;
	done: () => void;
	stepIndex: number;
	totalSteps: number;
}

export const RiskAssessmentSpotlightTour = ({
	dismiss,
	next,
	done,
	stepIndex,
	totalSteps,
}: RiskAssessmentSpotlightTourProps): JSX.Element => {
	const tourIndex = Object.keys(tour)[stepIndex] as string;
	const Step = tour[tourIndex] || (() => <></>);
	return (
		<Step dismiss={dismiss} next={next} done={done} stepIndex={stepIndex} totalSteps={totalSteps} />
	);
};
